import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { UserService } from '../../features/user/services/user.service';
import { User, UserCheckInFilter } from '../../features/user/services/user.types';
import { UserInfo } from '../../features/user/services/user-info.model';
import { SvcMasterdataService } from '../../services/svc-masterdata.service';
import { Observable, of } from 'rxjs';
import { SvcMasterdataEquipment } from '../../interfaces/svc-masterdata.interface';

export interface CheckinFilterSelection {
  udaId?: number | null;
  equipmentId?: number | null;
  udaName?: string | null;
  equipmentName?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CheckinFilterService {
  private readonly _selection = signal<CheckinFilterSelection | null>(null);
  readonly selection: Signal<CheckinFilterSelection | null> = this._selection.asReadonly();
  readonly selectionChanges$ = toObservable(this.selection);

  private readonly _hydrated = signal<boolean>(false);
  readonly hydrated: Signal<boolean> = this._hydrated.asReadonly();
  readonly hydratedChanges$ = toObservable(this.hydrated);

  private readonly _loading = signal<boolean>(true);
  readonly loading: Signal<boolean> = this._loading.asReadonly();
  readonly loadingChanges$ = toObservable(this.loading);

  private _fetchingFromCache = false;
  private _hydratingLabels = false;
  private _logoutPatchSent = false;
  private _lastUserId: number | null = null;

  constructor(
    private readonly _userService: UserService,
    private readonly _httpClient: HttpClient,
    private readonly _appConfig: AppEnvironmentConfig,
    private readonly _masterdataService: SvcMasterdataService
  ) {
    this._hydrateFromUser(this._userService.user);
    this._userService.user$.subscribe((user) => {
      if (user) {
        this._logoutPatchSent = false;
        this._lastUserId = (user as any)?.userId ?? null;
        this._hydrateFromUser(user);
        return;
      }

      // On logout/session clear, patch backend to clear check-in selection
      if (!this._logoutPatchSent && this._lastUserId !== null) {
        this._logoutPatchSent = true;
        const filter = this._userService.user?.checkInFilter;
        const wasDefault = (filter as any)?.setAsDefault === true || (typeof filter?.default === 'boolean' ? filter.default : false);
        if (!wasDefault) {
          this._userService
            .updateCheckinFilter({
              setAsDefault: false,
              dontAskAgain: false,
              udaId: null,
              equipmentId: null,
            })
            .pipe(take(1))
            .subscribe();
        }
      }
      this._lastUserId = null;
      this.clearSelection();
      this._loading.set(false);
    });
    if (!this.hasCompleteSelection()) {
      this._fetchFromCachedEndpoint().pipe(take(1)).subscribe();
    } else {
      this._loading.set(false);
    }
  }

  /**
   * Force a refresh from the cached user endpoint to hydrate the active check-in selection.
   */
  refreshFromCachedEndpoint(): Observable<CheckinFilterSelection | null> {
    return this._fetchFromCachedEndpoint(true);
  }

  hasCompleteSelection(): boolean {
    const selection = this._selection();
    const hasUda = selection?.udaId !== null && selection?.udaId !== undefined;
    return hasUda;
  }

  getSelectionSnapshot(): CheckinFilterSelection | null {
    return this._selection();
  }

  setSelection(selection: CheckinFilterSelection | null): void {
    this._setSelection(selection);
  }

  updateSelection(
    udaId?: number | null,
    equipmentId?: number | null,
    labels?: { udaName?: string | null; equipmentName?: string | null }
  ): void {
    const normalizedUdaId = this._normalizeId(udaId);
    const normalizedEquipmentId = this._normalizeId(equipmentId);
    const current = this._selection();

    const resolvedUdaName = this._sanitizeLabel(labels?.udaName ?? (normalizedUdaId && normalizedUdaId === current?.udaId ? current?.udaName ?? null : null));
    const resolvedEquipmentName =
      this._sanitizeLabel(labels?.equipmentName ?? (normalizedEquipmentId && normalizedEquipmentId === current?.equipmentId ? current?.equipmentName ?? null : null));

    this._setSelection({
      udaId: normalizedUdaId,
      equipmentId: normalizedEquipmentId,
      udaName: normalizedUdaId ? resolvedUdaName : null,
      equipmentName: normalizedEquipmentId ? resolvedEquipmentName : null,
    });
  }

  getFilterQueryParams(): { udaId: number; equipmentId: number | null } | null {
    const selection = this._selection();
    const hasUda = selection?.udaId !== null && selection?.udaId !== undefined;
    if (hasUda) {
      return {
        udaId: selection.udaId,
        equipmentId: selection.equipmentId ?? null,
      };
    }
    return null;
  }

  loadEquipmentsByLocation(placeId: number | string): Observable<SvcMasterdataEquipment[]> {
    if (placeId === null || placeId === undefined || placeId === '') {
      return of([]);
    }
    return this._masterdataService.getEquipmentsByLocation(Number(placeId));
  }

  clearSelection(): void {
    this._setSelection(null);
  }

  buildHttpParams(additional?: Record<string, string | number>): HttpParams | undefined {
    const filter = this.getFilterQueryParams();
    const paramsSource: Record<string, string | number> = { ...(additional ?? {}) };

    if (filter) {
      paramsSource['udaId'] = filter.udaId;
      paramsSource['equipmentId'] = filter.equipmentId;
    }

    if (!Object.keys(paramsSource).length) {
      return undefined;
    }

    let params = new HttpParams();
    Object.entries(paramsSource).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.append(key, value as any);
      }
    });

    return params;
  }

  private _normalizeId(value?: number | string | null): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private _sanitizeLabel(label?: string | null): string | null {
    if (!label) {
      return null;
    }
    const cleaned = label.replace(/<[^>]*>/g, '').trim();
    return cleaned || null;
  }

  private _hydrateFromUser(user: User | null | undefined): void {
    if (!user?.checkInFilter) {
      this._hydrated.set(true);
      this._loading.set(false);
      return;
    }
    const selection = this._resolveSelection(user.checkInFilter);
    if (selection) {
      this._setSelection(selection);
    }
    this._hydrated.set(true);
    this._loading.set(false);
  }

  private _fetchFromCachedEndpoint(force = false): Observable<CheckinFilterSelection | null> {
    if (this._fetchingFromCache) {
      return of(this._selection());
    }
    if (!force && this.hasCompleteSelection()) {
      return of(this._selection());
    }
    this._fetchingFromCache = true;
    this._loading.set(true);
    return this._httpClient
      .get<UserInfo>(`${this._appConfig.APIs.apiUrlUsers}/user/infos/cached`)
      .pipe(
        tap((info) => {
          const session = info?.session;
          this._syncCheckinFilterWithApi(session?.checkInFilter);
        }),
        map((info) => {
          const session = info?.session;
          const selection = this._resolveSelection(session?.checkInFilter);
          if (selection) {
            this._setSelection(selection);
            return selection;
          }
          return null;
        }),
        // AB#72477 - this endpoint can 400 (e.g. the user's area was deactivated); without
        // this, the unhandled error from an eager, no-error-callback subscribe() propagated
        // as an uncaught exception and broke unrelated parts of the page (disabled buttons).
        catchError(() => of(null)),
        finalize(() => {
          this._fetchingFromCache = false;
          this._hydrated.set(true);
          this._loading.set(false);
        })
      );
  }

  /**
   * Sync check-in filter back to API based on default/active info returned by cached GET.
   * - If default is false/absent, clear uda/equipment.
   * - If default is true or default object, send active values as default.
   */
  private _syncCheckinFilterWithApi(filter?: UserCheckInFilter | null): void {
    if (!filter) return;

    const defaultFlag =
      (filter as any)?.setAsDefault === true ||
      (typeof filter.default === 'boolean' ? filter.default : Boolean((filter.default as any)?.udaId || (filter.default as any)?.equipmentId));
    const activeUda = this._normalizeId(filter.active?.udaId);
    const activeEquipment = this._normalizeId(filter.active?.equipmentId);
    const dontAskAgain = filter.dontAskAgain ?? false;

    const payload = defaultFlag
      ? { setAsDefault: true, dontAskAgain, udaId: activeUda, equipmentId: activeEquipment }
      : { setAsDefault: false, dontAskAgain, udaId: null, equipmentId: null };

    this._userService.updateCheckinFilter(payload).pipe(take(1)).subscribe();
  }

  private _resolveSelection(filter?: UserCheckInFilter | null): CheckinFilterSelection | null {
    if (!filter) {
      return null;
    }
    const candidate =
      filter.active ??
      filter.default ??
      filter.recent?.find((item) => Boolean(item?.udaId) && Boolean(item?.equipmentId));
    if (!candidate) {
      return null;
    }
    const selection: CheckinFilterSelection = {
      udaId: this._normalizeId(candidate.udaId),
      equipmentId: this._normalizeId(candidate.equipmentId),
    };
    if (selection.udaId === null || selection.udaId === undefined) {
      this._loading.set(false);
      return null;
    }
    this._loading.set(false);
    return selection;
  }

  private _setSelection(selection: CheckinFilterSelection | null): void {
    this._selection.set(selection);
    this._hydrateUdaNameIfMissing(selection);
  }

  private _hydrateUdaNameIfMissing(selection: CheckinFilterSelection | null): void {
    if (this._hydratingLabels) return;
    if (!selection?.udaId || selection?.udaName) return;
    this._hydratingLabels = true;
    this._masterdataService
      .getLocations(true)
      .pipe(take(1))
      .subscribe({
        next: (locations) => {
          const match = locations?.find((location) => this._normalizeId(location?.udaId) === selection.udaId);
          const resolvedName = this._sanitizeLabel(match?.udaName ?? null);
          if (!resolvedName) return;
          const current = this._selection();
          if (!current) return;
          this._selection.set({
            ...current,
            udaName: current.udaId === selection.udaId ? resolvedName : current.udaName,
          });
        },
        complete: () => {
          this._hydratingLabels = false;
        },
        error: () => {
          this._hydratingLabels = false;
        },
      });
  }
}
